import React from "react";

/**
 * Catches any runtime error in the child tree so the user sees a
 * readable fallback instead of a silent black screen. Without this,
 * any ReferenceError / TypeError in a route (e.g. Settings, AuthCallback)
 * unmounts the whole router and leaves the dark background exposed.
 *
 * On error we also ship a beacon to POST /api/client-errors so the
 * Architect can review the failure in the Black Box Vault.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep this visible in the browser console for triage.
    console.error("[Sentinel ErrorBoundary]", error, info?.componentStack);
    this.report(error, info);
  }

  report = (error, info) => {
    if (!BACKEND_URL) return;
    try {
      const body = JSON.stringify({
        page: window.location.pathname + window.location.search,
        message: String(error?.message || error || "unknown"),
        stack:
          (error?.stack ? String(error.stack) : "") +
          (info?.componentStack ? `\n--- componentStack ---\n${info.componentStack}` : ""),
        user_agent: navigator?.userAgent || "",
        occurred_at: new Date().toISOString(),
      });
      // fetch + keepalive so the beacon survives a navigation/reload
      fetch(`${BACKEND_URL}/api/client-errors`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {}); // never cascade
    } catch {
      /* reporting must never throw */
    }
  };

  reset = () => {
    this.setState({ error: null });
    // Force a clean route — most render errors are route-specific.
    window.location.assign("/");
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#09090b] text-zinc-100 px-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full border border-rose-500/30 bg-[#0b0b0d] p-8">
          <div className="text-[10px] uppercase tracking-[0.35em] text-rose-400 mb-3">
            Sentinel · Safe Landing
          </div>
          <h1 className="font-heading text-2xl">Something misfired.</h1>
          <p className="text-xs text-zinc-400 mt-3 leading-relaxed">
            A panel on this page raised an unexpected error. The rest of the
            Sentinel is still armed — return to the console and try again. The
            Architect has been notified automatically.
          </p>
          <pre className="mt-4 text-[10px] text-zinc-500 border border-zinc-800 p-3 overflow-x-auto whitespace-pre-wrap max-h-40">
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <div className="mt-6 flex gap-3">
            <button
              onClick={this.reset}
              data-testid="error-boundary-home-btn"
              className="px-5 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 text-xs uppercase tracking-[0.3em]"
            >
              Return home
            </button>
            <button
              onClick={() => window.location.reload()}
              data-testid="error-boundary-retry-btn"
              className="px-5 py-3 border border-zinc-700 text-zinc-300 hover:border-zinc-500 text-xs uppercase tracking-[0.3em]"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
}
